import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { styled } from "@mui/material";
import { usePatient } from "../state/patientContext";
import { DocQuestion } from "../components/Questions/DocQuestion";
import { useTranslate } from "../hooks/useTranslate";
import { ClosedSingleQuestionView } from "../components/Questions/closedSingleQuestionView";
import { PanelQuestionStatus } from "../types/PatientBackgroundInformation";
import { SelectionType } from "../components/uiControllers/AnswerButton";
import { TextAnswer } from "../components/answers/textAnswer";
import { AnswerContainer } from "../components/answers/answerElement";
import { QuestionContents } from "../components/Questions/questionElement";
import { PanelMultiSelectWithSearch } from "./panelMultiSelectWithSearch";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useWidgetSettings } from "../widget/useWidgetSettings";
import { Answer } from "../solverHooks/useConversation";
import { QualifiedFeature } from "fe-common/graphql";

//region [[ Styles ]]

const PatientPanelView = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface PatientPanelProps {
  qText: [string, string];
  onDone?: (selected: QualifiedFeature[], skipped: boolean) => void;
  answer: Answer;
  suggestions: QualifiedFeature[];
  askBeforePresentingQuestion: boolean;
  skipButtonText?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

/* Renders a panel as a multi select question */
export const PatientPanel = ({
  onDone,
  answer,
  suggestions,
  qText,
  askBeforePresentingQuestion,
  skipButtonText,
  ...props
}: PatientPanelProps) => {
  const [firstText, secondText] = qText;

  const { partnerId } = useWidgetSettings();

  const { gender } = usePatient();

  const { translate } = useTranslate();

  const [panelStatus, setPanelStatus] = useState(() => getPanelStatus(answer));

  const scrollRef = useRef(null);

  useEffect(() => {
    if (!answer) {
      setPanelStatus(
        askBeforePresentingQuestion
          ? PanelQuestionStatus.Initial
          : PanelQuestionStatus.Prompting,
      );
    }
  }, [answer, askBeforePresentingQuestion]);

  // useEffect(() => {
  //   if (scrollRef.current) {
  //     scrollRef.current.scrollIntoView({
  //       behavior: "smooth",
  //       block: "end",
  //       inline: "end",
  //     });
  //   }
  // }, [panelStatus]);

  const handlePanelInitialEngagement = useCallback(
    (selectionType) => {
      evt({
        category: CATEGORY.PATIENT_CHAT,
        action: ACTION.PATIENT_ANSWERED_QUESTION,
        label: selectionType,
        partnerId,
      });
      // If user chose to skip, continue to next panel in list
      if (selectionType === SelectionType.NO) {
        setPanelStatus(PanelQuestionStatus.Skipped);
        onDone([], true);
      } else {
        setPanelStatus(PanelQuestionStatus.Prompting);
      }
    },
    [onDone, partnerId],
  );

  const handleDoneWithSelection = useCallback(
    (selectedFeatures: QualifiedFeature[]) => {
      onDone(selectedFeatures, false);
      setPanelStatus(PanelQuestionStatus.Answered);
    },
    [onDone],
  );

  const selectedFeatures = answer?.qfs.filter((qf) => !qf.skip);

  return (
    <PatientPanelView className={"patient-panel-view"}>
      {askBeforePresentingQuestion && <DocQuestion text={firstText} />}
      {panelStatus === PanelQuestionStatus.Initial && (
        <ClosedSingleQuestionView
          gender={gender}
          onClick={handlePanelInitialEngagement}
          ref={scrollRef}
        />
      )}
      {panelStatus === PanelQuestionStatus.Prompting && (
        <>
          {askBeforePresentingQuestion && (
            <AnswerContainer>
              <TextAnswer
                answerText={translate("ANSWER_YES")}
                gender={gender}
              />
            </AnswerContainer>
          )}
          <QuestionContents className={"question-contents"} ref={scrollRef}>
            <PanelMultiSelectWithSearch
              questionText={secondText}
              suggested={suggestions}
              selected={selectedFeatures}
              onDone={handleDoneWithSelection}
              skipButtonOverrideText={skipButtonText}
              hideDescription
            />
          </QuestionContents>
        </>
      )}
      {panelStatus === PanelQuestionStatus.Answered && (
        <>
          {askBeforePresentingQuestion && (
            <AnswerContainer>
              <TextAnswer
                answerText={translate("ANSWER_YES")}
                gender={gender}
              />
            </AnswerContainer>
          )}
          <DocQuestion text={secondText} />
        </>
      )}
      {panelStatus === PanelQuestionStatus.Skipped && (
        <DocQuestion text={secondText} />
      )}
    </PatientPanelView>
  );
};

function getPanelStatus(answer: Answer): PanelQuestionStatus {
  if (!answer) return PanelQuestionStatus.Initial;
  if (answer.skipped) return PanelQuestionStatus.Skipped;
  return PanelQuestionStatus.Answered;
}
